import React from 'react';
import { Link, NavigateFunction } from 'react-router-dom';
import { openAppRouteInNewWindow } from '../../../shared/utils/openAppRouteInNewWindow';
import { type MainMapView } from './mapUtils';

const NAV_ITEMS = [
  'Dispatch',
  'Ressources',
  'Bâtiments',
  'Synoptique interventions',
  'Historique',
  'Technologies',
  'Alliances',
  'Formations',
  'Météo',
] as const;

const FUTURE_ITEMS = new Set(['Alliances', 'Formations', 'Météo']);

const VIEW_MAP: Record<string, MainMapView> = {
  Dispatch: 'dispatch',
  Ressources: 'resources',
  'Bâtiments': 'buildings',
  'Synoptique interventions': 'interventions',
  Historique: 'historique',
  Technologies: 'tech-tree',
};

interface MapHeaderProps {
  currentUser: {
    username: string;
    argents?: number | null;
    medailles?: number | null;
    roles_id?: number | null;
  };
  headerClock: string;
  showInterventionSectors: boolean;
  setShowInterventionSectors: React.Dispatch<React.SetStateAction<boolean>>;
  priseAppels: boolean;
  priseLoadError: string | null;
  priseToggling: boolean;
  togglePriseAppels: () => Promise<void>;
  activeMainView: MainMapView;
  setMainView: (view: MainMapView) => void;
  navigate: NavigateFunction;
  hasPanelEntryAccess: boolean;
  handleFutureFeature: (label: string) => void;
}

const MapHeader: React.FC<MapHeaderProps> = ({
  currentUser,
  headerClock,
  showInterventionSectors,
  setShowInterventionSectors,
  priseAppels,
  priseLoadError,
  priseToggling,
  togglePriseAppels,
  activeMainView,
  setMainView,
  navigate,
  hasPanelEntryAccess,
  handleFutureFeature,
}) => (
  <header className="absolute inset-x-0 top-0 z-[1200] border-b border-slate-600/45 bg-gradient-to-b from-slate-700/95 to-slate-900/95 backdrop-blur-md">
    <div className="flex h-14 items-center justify-between px-4">
      <div className="flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/images/logo.png" alt="Logo" className="h-8 w-8 rounded-sm object-contain" />
          <div>
            <p className="text-[9px] uppercase tracking-[0.22em] text-slate-300">Centre de commandement</p>
            <p className="text-sm font-bold tracking-wide text-white">OPSECOURS WEB</p>
          </div>
        </Link>
        <div className="ml-2 flex items-center gap-2 border-l border-slate-600/70 pl-3 text-[11px]">
          <div className="rounded-md border border-emerald-500/40 bg-emerald-900/25 px-2 py-1 font-semibold text-emerald-100">
            € {Number(currentUser.argents ?? 0).toLocaleString('fr-FR')}
          </div>
          <div className="rounded-md border border-amber-500/40 bg-amber-900/20 px-2 py-1 font-semibold text-amber-100">
            🏅 {Number(currentUser.medailles ?? 0).toLocaleString('fr-FR')}
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-end overflow-x-auto">
        <div className="flex min-w-max items-center gap-2 py-1 pl-3 text-[11px]">
          <time className="hidden text-slate-300 sm:block">{headerClock}</time>

          <div className="flex items-center gap-2 rounded-md border border-slate-600/60 bg-slate-800/70 px-2 py-1">
            <span className={`text-[10px] font-semibold uppercase tracking-wide ${showInterventionSectors ? 'text-emerald-300' : 'text-amber-300'}`}>
              Secteurs {showInterventionSectors ? 'ON' : 'OFF'}
            </span>
            <button
              type="button"
              id="map-toggle-sectors"
              role="switch"
              aria-checked={showInterventionSectors}
              onClick={() =>
                setShowInterventionSectors((prev) => {
                  const next = !prev;
                  localStorage.setItem('mapShowInterventionSectors', next ? 'true' : 'false');
                  return next;
                })
              }
              className={`relative h-5 w-10 rounded-full transition-colors ${showInterventionSectors ? 'bg-emerald-600' : 'bg-slate-600'}`}
            >
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${showInterventionSectors ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>

          <div className="flex items-center gap-2 rounded-md border border-slate-600/60 bg-slate-800/70 px-2 py-1">
            <span className={`text-[10px] font-semibold uppercase tracking-wide ${priseAppels ? 'text-emerald-300' : 'text-amber-300'}`}>
              Appels {priseAppels ? 'ON' : 'OFF'}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={priseAppels}
              onClick={() => void togglePriseAppels()}
              disabled={priseToggling}
              className={`relative h-5 w-10 rounded-full transition-colors disabled:opacity-50 ${priseAppels ? 'bg-emerald-600' : 'bg-slate-600'}`}
            >
              <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${priseAppels ? 'left-5' : 'left-0.5'}`} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="rounded-md border border-slate-500/60 bg-slate-800/70 px-2 py-1 font-semibold text-slate-100 hover:bg-slate-700/80"
          >
            Retour dashboard
          </button>

          {hasPanelEntryAccess && (
            <button
              type="button"
              onClick={() => openAppRouteInNewWindow('/panel')}
              className="rounded-md border border-indigo-500/60 bg-indigo-600/25 px-2 py-1 font-semibold text-indigo-100 hover:bg-indigo-600/35"
            >
              Accès panel
            </button>
          )}

          <div className="rounded-md border border-emerald-500/50 bg-emerald-500/15 px-2 py-1 font-semibold uppercase tracking-wide text-emerald-100">
            {currentUser.username} · en ligne
          </div>

          {priseLoadError && (
            <span className="hidden text-[10px] text-red-300 xl:inline">{priseLoadError}</span>
          )}
        </div>
      </div>
    </div>

    <div className="border-t border-slate-700/60 px-2 py-1">
      <div className="overflow-x-auto">
        <nav className="flex min-w-max items-center gap-1 text-[11px]">
          {NAV_ITEMS.map((item) => {
            const isFuture = FUTURE_ITEMS.has(item);
            const targetView = VIEW_MAP[item];
            const isActive = targetView && activeMainView === targetView;
            return (
              <button
                key={item}
                type="button"
                onClick={() => {
                  if (targetView) { setMainView(targetView); return; }
                  handleFutureFeature(item);
                }}
                className={`rounded-md border px-2.5 py-1 font-semibold uppercase tracking-wide transition-colors ${
                  isActive
                    ? 'border-emerald-500/60 bg-emerald-500/20 text-emerald-100'
                    : isFuture
                      ? 'border-slate-700/60 bg-slate-800/20 text-slate-500 hover:text-slate-400'
                      : 'border-slate-600/50 bg-slate-800/35 text-slate-300 hover:text-white'
                }`}
              >
                {item}{isFuture ? ' (bientot)' : ''}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  </header>
);

export default MapHeader;
